<template>
  <div
    role="group"
    :aria-label="t('dashboard.portfolio.table.filter_chips.group_label')"
    class="filter-chips d-flex align-items-center flex-wrap flex-1 min-w-0 row-gap-2 py-1"
  >
    <span
      class="filter-chips-line d-inline-flex align-items-center fw-semibold text-body-tertiary"
    >
      {{ t("dashboard.portfolio.table.filter_chips.label") }}
    </span>
    <span
      v-for="chip in chips"
      :key="chip.column"
      class="filter-chip d-inline-flex align-items-center rounded-pill fw-semibold text-nowrap"
      :class="chipClass(chip.tag)"
    >
      {{ chip.text }}
      <span
        v-if="chip.tag"
        class="filter-chip-tag micro-label text-uppercase fw-bolder bg-white rounded-pill"
        :class="
          chip.tag === 'unavailable'
            ? 'text-danger-emphasis'
            : 'text-body-tertiary'
        "
      >
        {{ t(TAG_LABEL_KEYS[chip.tag]) }}
      </span>
      <button
        type="button"
        class="filter-chip-remove d-inline-flex align-items-center justify-content-center p-0 border-0 rounded-circle"
        :aria-label="chip.removeLabel"
        @click="$emit('remove', chip.column)"
      >
        <FontAwesomeIcon icon="xmark" />
      </button>
    </span>
    <span
      v-if="chips.length === 0"
      class="filter-chips-line d-inline-flex align-items-center text-body-tertiary"
    >
      {{ t("dashboard.portfolio.table.filter_chips.none") }}
    </span>
    <button
      v-if="trailingAction"
      type="button"
      class="filter-chips-line d-inline-flex align-items-center btn btn-link p-0 fw-semibold"
      @click="$emit('reset')"
    >
      {{ t(TRAILING_LABEL_KEYS[trailingAction]) }}
    </button>
  </div>
</template>

<script setup lang="ts">
import type {
  FilterChip,
  FilterChipTag,
  TrailingFilterAction,
} from "@/dashboard/inventory_management/common/portfolioFilterChips";
import type { FilterColumn } from "@/dashboard/inventory_management/common/portfolioTableFilters";
import { useI18n } from "vue-i18n";

defineProps<{
  chips: FilterChip[];
  trailingAction: TrailingFilterAction | null;
}>();

defineEmits<{
  remove: [column: FilterColumn];
  reset: [];
}>();

const { t } = useI18n();

const TAG_LABEL_KEYS: Record<FilterChipTag, string> = {
  default: "dashboard.portfolio.table.filter_chips.tag_default",
  document: "dashboard.portfolio.table.filter_chips.tag_document",
  unavailable: "dashboard.portfolio.table.filter_chips.tag_unavailable",
};

// "Clear all" where the page has no defaults: resetting then empties every column.
const TRAILING_LABEL_KEYS: Record<TrailingFilterAction, string> = {
  reset: "dashboard.portfolio.table.filter_chips.reset",
  clear: "dashboard.portfolio.table.filter_chips.clear_all",
};

// Utility colours are !important, so each tone picks its own rather than overriding a base one.
const chipClass = (tag: FilterChipTag | null) => {
  if (tag === "unavailable") {
    return "filter-chip--danger bg-danger-subtle text-danger-emphasis";
  }
  // Default / From document: a lighter chip, so the user's own filters stand out.
  if (tag) return "filter-chip--tagged text-primary";
  return "bg-primary-subtle text-primary";
};
</script>

<style scoped lang="scss">
// Chips wrap in their own area; the 4px vertical padding centres a 28px chip line against the
// 36px column selector beside it.
.filter-chips {
  column-gap: 10px;
  font-size: 0.8125rem;
}

.filter-chips-line {
  height: 28px;
  // A .btn sets its own size; the plain text lines take the area's already.
  font-size: inherit;
}

.filter-chip {
  gap: 6px;
  height: 28px;
  padding: 0 4px 0 12px;
  border: 1px solid var(--phoenix-primary-bg-subtle);
  font-size: 0.75rem;

  &--tagged {
    border-color: rgba(var(--phoenix-primary-rgb), 0.2);
    background: rgba(var(--phoenix-primary-rgb), 0.05);
  }

  &--danger {
    border-color: var(--phoenix-danger-border-subtle);
  }
}

.filter-chip-tag {
  padding: 1px 6px;
}

// No bg-transparent: its !important would beat the hover tint.
.filter-chip-remove {
  width: 22px;
  height: 22px;
  background: none;
  color: inherit;
  font-size: 0.625rem;

  &:hover {
    background: rgba(var(--phoenix-primary-rgb), 0.1);
  }
}
</style>
